import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Plus } from 'lucide-react';
import { formatDate } from '@/shared/lib/format';
import { PageHeader } from '@/shared/components/page-header';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import type { IntegrationProvider } from '@/shared/types/nps';
import { useAvailableProviders, useIntegrations } from '../api/use-nps';
import { ProviderLogo } from './provider-logo';
import { IntegrationConnectDialog } from '../components/integrations/integration-connect-dialog';

export function IntegrationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const integrationsQuery = useIntegrations();
  const providersQuery = useAvailableProviders();
  const [connecting, setConnecting] = useState<IntegrationProvider | null>(null);

  const integrations = integrationsQuery.data ?? [];
  const providers = providersQuery.data ?? [];
  const connectedTypes = new Set(integrations.map((i) => i.provider.type));

  return (
    <>
      <PageHeader
        title="Integrações"
        description="Conecte sua loja para coletar respostas automaticamente."
        actions={<Button onClick={() => navigate('/nps/integracoes/nova')}><Plus className="size-4" /> Nova integração</Button>}
      />

      <h2 className="mb-3 text-sm font-semibold">Conectadas</h2>
      {integrationsQuery.isLoading ? (
        <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
      ) : integrations.length === 0 ? (
        <Card className="mb-8 p-6 text-sm text-muted-foreground">Nenhuma integração conectada. Comece conectando um provedor.</Card>
      ) : (
        <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {integrations.map((integ, i) => (
            <motion.div key={integ.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Card onClick={() => navigate(`/nps/integracoes/${integ.id}`)} className="group cursor-pointer p-5 transition-all hover:-translate-y-1 hover:shadow-glass-sm">
                <div className="flex items-center justify-between">
                  <ProviderLogo type={integ.provider.type} />
                  <Badge variant="success" className="font-normal">Ativa</Badge>
                </div>
                <h3 className="mt-4 font-semibold">{integ.provider.name}</h3>
                {integ.store_url ? <p className="truncate text-xs text-muted-foreground">{integ.store_url}</p> : null}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{(integ.integration_webhooks ?? []).length} webhook(s) · {formatDate(integ.created_at)}</span>
                  <ArrowRight className="size-4 opacity-50 group-hover:translate-x-0.5 group-hover:opacity-100" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <h2 className="mb-3 text-sm font-semibold">Disponíveis</h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {providers.map((p) => {
          const connected = connectedTypes.has(p.type);
          return (
            <Card key={p.id} className="flex h-full flex-col items-start p-5">
              <ProviderLogo type={p.type} />
              <h3 className="mt-4 font-semibold">{p.name}</h3>
              <p className="mt-1 line-clamp-2 flex-1 text-xs text-muted-foreground">{p.description}</p>
              <Button
                variant={connected ? 'secondary' : 'primary'}
                size="sm"
                className="mt-4 w-full"
                disabled={connected}
                onClick={() => setConnecting(p)}
              >
                {connected ? <><CheckCircle2 className="size-4" /> Conectada</> : <><Plus className="size-4" /> Conectar</>}
              </Button>
            </Card>
          );
        })}
      </div>

      <IntegrationConnectDialog
        provider={connecting}
        open={Boolean(connecting)}
        onOpenChange={(o) => !o && setConnecting(null)}
        onConnected={() => queryClient.invalidateQueries({ queryKey: ['nps', 'integrations'] })}
      />
    </>
  );
}
